import { useMemo, useState, type CSSProperties } from 'react';
import { Avatar, ErrorState, Modal, TableSkeleton } from '@/components/ui';
import { IconCheck, IconSearch } from '@/components/Icons';
import { useLinkAccess, useSetLinkAccess, useUsers } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';
import type { LinkAccess, MemberLink, User } from '@/lib/types';

interface Props {
  link: MemberLink;
  onClose: () => void;
}

const sameText = (text: string, query: string) =>
  text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().includes(query);

const normalize = (query: string) => query.trim().normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

/**
 * Quem recebe a oferta (administrador): todos os membros, ou só os escolhidos.
 * Administradores sempre recebem todas. Tirar alguém não quebra o link que ele
 * já mandou — só some da página dele.
 */
export function LinkAccessModal({ link, onClose }: Props) {
  const access = useLinkAccess(link.key);
  const users = useUsers({});
  const members = useMemo(
    () => (users.data ?? []).filter((user) => user.role !== 'ADMIN' && user.active).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [users.data],
  );
  const error = access.error ?? users.error;

  if (access.data && users.data) return <AccessForm link={link} initial={access.data} members={members} onClose={onClose} />;
  return (
    <Modal title={TITLE} width={560} onClose={onClose}>
      <OfferHeading link={link} />
      {error ? <ErrorState message={(error as Error).message} /> : <TableSkeleton rows={4} columns={2} />}
    </Modal>
  );
}

const TITLE = 'Quem recebe esta oferta';

function OfferHeading({ link }: { link: MemberLink }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div className="muted" style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {link.group} · {link.productName}
      </div>
      <div style={{ fontSize: 14, fontWeight: 500, marginTop: 3 }}>{link.label}</div>
    </div>
  );
}

interface FormProps {
  link: MemberLink;
  initial: LinkAccess;
  members: User[];
  onClose: () => void;
}

function AccessForm({ link, initial, members, onClose }: FormProps) {
  const toast = useToast();
  const save = useSetLinkAccess();
  const [restricted, setRestricted] = useState(initial.restricted);
  const [picked, setPicked] = useState(() => new Set(initial.memberIds));
  const [query, setQuery] = useState('');

  const needle = normalize(query);
  const shown = needle ? members.filter((member) => sameText(member.name, needle) || sameText(member.login, needle)) : members;
  const pickedCount = members.filter((member) => picked.has(member.id)).length;

  function toggle(id: string) {
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function pickShown(on: boolean) {
    setPicked((current) => {
      const next = new Set(current);
      for (const member of shown) {
        if (on) next.add(member.id);
        else next.delete(member.id);
      }
      return next;
    });
  }

  async function submit() {
    // Só vão os membros que aparecem aqui: quem foi desativado sai da lista junto.
    const memberIds = restricted ? members.filter((member) => picked.has(member.id)).map((member) => member.id) : [];
    try {
      await save.mutateAsync({ key: link.key, restricted, memberIds });
      toast.success(
        !restricted
          ? 'A oferta volta a aparecer para todos os membros.'
          : memberIds.length === 0
            ? 'A oferta saiu da página de todos os membros.'
            : `A oferta agora só aparece para ${memberIds.length} ${memberIds.length === 1 ? 'membro' : 'membros'}.`,
      );
      onClose();
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  return (
    <Modal
      title={TITLE}
      width={560}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={save.isPending}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={save.isPending}>
            {save.isPending ? <span className="spinner" /> : <IconCheck />} Salvar
          </button>
        </>
      }
    >
      <OfferHeading link={link} />
      <div role="radiogroup" aria-label="Quem recebe" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
        <ModeOption checked={!restricted} onSelect={() => setRestricted(false)} title="Todos os membros" hint="Quem entrar depois também recebe." />
        <ModeOption checked={restricted} onSelect={() => setRestricted(true)} title="Só os escolhidos" hint="Os outros deixam de ver o link." />
      </div>

      {restricted ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '1 1 200px' }}>
              <span className="muted" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', display: 'flex' }}>
                <IconSearch />
              </span>
              <input
                className="inp"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar membro"
                aria-label="Buscar membro"
                style={{ paddingLeft: 32, width: '100%' }}
              />
            </div>
            <button type="button" className="btn btn-sm" onClick={() => pickShown(true)} disabled={shown.length === 0}>Marcar todos</button>
            <button type="button" className="btn btn-sm" onClick={() => pickShown(false)} disabled={shown.length === 0}>Nenhum</button>
          </div>

          <div style={listStyle} role="group" aria-label="Membros">
            {members.length === 0 ? (
              <p className="muted" style={{ fontSize: 12.5, margin: 0, padding: 14 }}>Nenhum membro ativo ainda.</p>
            ) : shown.length === 0 ? (
              <p className="muted" style={{ fontSize: 12.5, margin: 0, padding: 14 }}>Ninguém com esse nome.</p>
            ) : (
              shown.map((member) => (
                <label key={member.id} style={rowStyle}>
                  <input type="checkbox" checked={picked.has(member.id)} onChange={() => toggle(member.id)} />
                  <Avatar name={member.name} color={member.avatarColor} photo={member.photoUrl} size={26} />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span className="truncate" style={{ display: 'block', fontSize: 13, fontWeight: 500 }}>{member.name}</span>
                    <span className="muted truncate" style={{ display: 'block', fontSize: 11.5 }}>{member.login}</span>
                  </span>
                </label>
              ))
            )}
          </div>

          <div className="muted" role="status" style={{ fontSize: 12 }}>
            {pickedCount === 0
              ? 'Ninguém escolhido: a oferta some da página de todos os membros.'
              : `${pickedCount} de ${members.length} ${members.length === 1 ? 'membro' : 'membros'} ${pickedCount === 1 ? 'recebe' : 'recebem'} esta oferta.`}
          </div>
        </div>
      ) : null}

      <p className="muted" style={{ fontSize: 12, margin: 0, lineHeight: 1.55 }}>
        Administradores sempre recebem todas as ofertas. Quem perde o acesso deixa de ver o link, mas o endereço que já mandou continua abrindo.
      </p>
    </Modal>
  );
}

function ModeOption({ checked, onSelect, title, hint }: { checked: boolean; onSelect: () => void; title: string; hint: string }) {
  return (
    <label
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '11px 13px',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}`,
        background: checked ? 'color-mix(in oklab, var(--primary) 8%, transparent)' : 'transparent',
        cursor: 'pointer',
        transition: 'border-color 150ms var(--ease-out), background-color 150ms var(--ease-out)',
      }}
    >
      <input type="radio" name="link-access-mode" checked={checked} onChange={onSelect} style={{ marginTop: 2 }} />
      <span>
        <span style={{ display: 'block', fontSize: 13, fontWeight: 500 }}>{title}</span>
        <span className="muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>{hint}</span>
      </span>
    </label>
  );
}

const listStyle: CSSProperties = {
  maxHeight: 'min(320px, 42vh)',
  overflowY: 'auto',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 12px',
  borderBottom: '1px solid var(--border)',
  cursor: 'pointer',
};
